import { useToast } from "./use-toast"
import {
  Toast,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
} from "./toast"
import { styled } from '@mui/material/styles'
import { Portal } from '@mui/material'

const StyledToast = styled(Toast)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(26, 26, 26, 0.95)' : 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(20px)',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
  borderRadius: '12px',
  padding: '12px',
  color: theme.palette.mode === 'dark' ? '#4ADE80' : '#166534',
  fontSize: '14px',
  lineHeight: 1.4,
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: theme.palette.mode === 'dark' 
    ? '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05)'
    : '0 8px 32px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 0, 0, 0.05)',
  '&:hover': {
      transform: 'translateX(4px)',
  },
  '&[data-state="open"]': {
    animation: 'slideIn 150ms cubic-bezier(0.16, 1, 0.3, 1)',
  },
  '&[data-state="closed"]': {
    animation: 'fadeOut 100ms ease-in',
  },
  '&[data-swipe="move"]': {
    transform: 'translateX(var(--radix-toast-swipe-move-x))',
  },
  '&[data-swipe="cancel"]': {
    transform: 'translateX(0)',
    transition: 'transform 200ms ease-out',
  },
  '&[data-swipe="end"]': {
    animation: 'swipeOut 100ms ease-out',
  },
  '@keyframes slideIn': {
    from: {
      transform: 'translateX(calc(100% + var(--viewport-padding)))',
    },
    to: {
      transform: 'translateX(0)',
    },
  },
  '@keyframes swipeOut': {
    from: {
      transform: 'translateX(var(--radix-toast-swipe-end-x))',
    },
    to: {
      transform: 'translateX(calc(100% + var(--viewport-padding)))',
    },
  },
  '@keyframes fadeOut': {
    from: {
      opacity: 1,
    },
    to: {
      opacity: 0,
    },
  }
}))

const StyledTitle = styled(ToastTitle)(({ theme }) => ({
  fontWeight: 600,
  marginBottom: '4px',
  color: theme.palette.mode === 'dark' ? '#4ADE80' : '#166534'
}))

const StyledDescription = styled(ToastDescription)(({ theme }) => ({
  margin: 0,
  opacity: 0.9,
  color: theme.palette.mode === 'dark' ? '#4ADE80' : '#166534'
}))

const StyledClose = styled(ToastClose)(({ theme }) => ({
  position: 'absolute',
  right: '8px',
  top: '8px',
  background: 'none',
  border: 'none',
  padding: '4px',
  cursor: 'pointer',
  color: theme.palette.mode === 'dark' ? '#4ADE80' : '#166534',
  opacity: 0.5,
  '&:hover': {
    opacity: 1,
    color: theme.palette.mode === 'dark' ? '#4ADE80' : '#166534',
  }
}))

const StyledViewport = styled(ToastViewport)(({ theme }) => ({
  position: 'fixed',
  bottom: '10px',
  right: '10px',
  display: 'flex',
  flexDirection: 'column',
  padding: '16px',
  gap: '8px',
  width: '400px',
  maxHeight: 'calc(100vh - 96px)',
  margin: 0,
  listStyle: 'none',
  zIndex: 99999999,
  outline: 'none',
  [theme.breakpoints.down('sm')]: {
    right: '16px',
  }
}))

export function Toaster() {
  const { toasts } = useToast()

  if (toasts.length === 0) return null

  return (
    <Portal>
      <ToastProvider swipeDirection="right" duration={4000}>
        {toasts.map(function ({ id, title, description, action, ...props }) {
          return (
            <StyledToast key={id} {...props}>
              <div className="grid gap-1">
                {title && <StyledTitle>{title}</StyledTitle>}
                {description && <StyledDescription>{description}</StyledDescription>}
              </div>
              {action}
              <StyledClose />
            </StyledToast>
          )
        })}
        <StyledViewport />
      </ToastProvider>
    </Portal>
  )
}